export const ISO_WEEKDAY_OPTIONS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 }
];

export const ALL_WEEKDAYS = ISO_WEEKDAY_OPTIONS.map(({ value }) => value);
export const DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5];
export const DEFAULT_SENDING_FULL_DAY = false;
export const DEFAULT_SENDING_START_TIME = '09:00';
export const DEFAULT_SENDING_END_TIME = '17:00';
export const MAX_EMAIL_SUBJECT_LENGTH = 200;
export const MAX_EMAIL_BODY_LENGTH = 10000;

export function isAdvancedCampaignCapabilityEnabled(data) {
  return data?.capabilities?.advancedCampaignSending === true;
}

export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function createNewCampaignScheduleDefaults() {
  return {
    sendingWeekdays: [...DEFAULT_WEEKDAYS],
    sendingFullDay: DEFAULT_SENDING_FULL_DAY,
    sendingStartTime: DEFAULT_SENDING_START_TIME,
    sendingEndTime: DEFAULT_SENDING_END_TIME,
    campaignTimezone: getBrowserTimezone()
  };
}

export function resolvePersistedCampaignSchedule(campaign) {
  const sendingWindow = campaign?.sendingWindow || {};
  return {
    sendingWeekdays:
      Array.isArray(campaign?.sendingWeekdays) && campaign.sendingWeekdays.length > 0
        ? [...campaign.sendingWeekdays]
        : [...ALL_WEEKDAYS],
    sendingFullDay: sendingWindow.fullDay !== false,
    sendingStartTime: minuteToTimeString(sendingWindow.startMinute, '08:00'),
    sendingEndTime: minuteToTimeString(sendingWindow.endMinute, '20:00'),
    campaignTimezone: sendingWindow.timezone || getBrowserTimezone()
  };
}

export function getIanaTimezoneOptions() {
  const browserTimezone = getBrowserTimezone();
  const supported = typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [
        'UTC',
        'Europe/London',
        'Europe/Paris',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Asia/Dubai',
        'Asia/Kolkata',
        'Asia/Singapore',
        'Asia/Tokyo',
        'Australia/Sydney'
      ];
  return Array.from(new Set([browserTimezone, 'UTC', ...supported])).sort();
}

export function timeStringToMinute(value) {
  if (!/^\d{2}:\d{2}$/.test(value || '')) return null;
  const [hour, minute] = value.split(':').map(Number);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

export function minuteToTimeString(value, fallback) {
  if (!Number.isInteger(value) || value < 0 || value >= 24 * 60) return fallback;
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

export function isAdvancedAccountAvailable(account) {
  return Boolean(
    account
    && account.isAuthorized !== false
    && account.needsReauth !== true
    && account.sendingPaused !== true
    && account.sendingError !== 'gmail_auth'
  );
}

export function validateAdvancedCampaignForm({
  gmailAccounts,
  sendingWeekdays,
  fullDay,
  startTime,
  endTime,
  timezone
}) {
  if (!Array.isArray(gmailAccounts) || gmailAccounts.length < 1 || gmailAccounts.length > 10) {
    return { valid: false, error: 'Select between 1 and 10 Gmail accounts.' };
  }
  const normalized = gmailAccounts.map((email) => String(email).trim().toLowerCase());
  if (new Set(normalized).size !== normalized.length) {
    return { valid: false, error: 'The same Gmail account cannot be selected twice.' };
  }
  if (!Array.isArray(sendingWeekdays) || sendingWeekdays.length < 1) {
    return { valid: false, error: 'Select at least one sending weekday.' };
  }
  if (!timezone) {
    return { valid: false, error: 'Select a campaign timezone.' };
  }

  if (fullDay) {
    return {
      valid: true,
      value: {
        gmailAccounts,
        sendingWeekdays: Array.from(new Set(sendingWeekdays)).sort((a, b) => a - b),
        sendingWindow: {
          timezone,
          fullDay: true,
          startMinute: 0,
          endMinute: 1440
        }
      }
    };
  }

  const startMinute = timeStringToMinute(startTime);
  const endMinute = timeStringToMinute(endTime);
  if (startMinute === null || endMinute === null) {
    return { valid: false, error: 'Enter valid start and end times.' };
  }
  if (startMinute === endMinute) {
    return { valid: false, error: 'Start and end times cannot be equal. Use full-day mode instead.' };
  }
  if (startMinute > endMinute) {
    return { valid: false, error: 'Overnight sending windows are not supported.' };
  }

  return {
    valid: true,
    value: {
      gmailAccounts,
      sendingWeekdays: Array.from(new Set(sendingWeekdays)).sort((a, b) => a - b),
      sendingWindow: {
        timezone,
        fullDay: false,
        startMinute,
        endMinute
      }
    }
  };
}

export function validateAdvancedCampaignContentForm({ emailSubject, emailBody }) {
  const subject = typeof emailSubject === 'string' ? emailSubject.trim() : '';
  const body = typeof emailBody === 'string' ? emailBody.trim() : '';
  if (!subject) {
    return { valid: false, error: 'Email subject is required.' };
  }
  if (subject.length > MAX_EMAIL_SUBJECT_LENGTH) {
    return {
      valid: false,
      error: `Email subject must be ${MAX_EMAIL_SUBJECT_LENGTH} characters or fewer.`
    };
  }
  if (!body) {
    return { valid: false, error: 'Email content is required.' };
  }
  if (body.length > MAX_EMAIL_BODY_LENGTH) {
    return {
      valid: false,
      error: `Email content must be ${MAX_EMAIL_BODY_LENGTH} characters or fewer.`
    };
  }
  return {
    valid: true,
    value: {
      emailSubject: subject,
      emailBody: body
    }
  };
}
